import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoginResult } from '../models/loginResult';
import { AuthService } from './auth.service';
import { AppEvent, AppEventType, EventQueueService } from './eventqueue.service';
import { MyConfigService } from './my-config.service';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private hostPath: string = JSON.parse(environment.IDENTITY);
  private refreshInProgress = false;

  constructor(
    private http: HttpClient,
    public router: Router,
    public authService: AuthService,
    public eventQueue: EventQueueService,
    private myConfigService: MyConfigService
  ) { }

  public activateUser(code: string, password: string): Observable<unknown> {
    const data = { site: 'www.shuttlebuss.se', code, password };
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    return this.http.post(this.hostPath + 'identity/user/activate', data, { headers }).pipe(
      map((result: any) => {
        const loginResult = new LoginResult();
        loginResult.success = result.success;
        loginResult.userCreateStatus = result.userCreateStatus;
        loginResult.status = result.status;
        return loginResult;
      }),
      catchError((error: any) => {
        const loginResult = new LoginResult();
        const result = error;
        loginResult.success = result.success;
        loginResult.userCreateStatus = result.userCreateStatus;
        loginResult.status = result.status;
        return throwError(loginResult);
      }),
      finalize(() => { })
    );
  }

  public refreshToken(): void {
    if (this.refreshInProgress) {
      return;
    }

    this.refreshInProgress = true;
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    this.http
      .get<any>(this.hostPath + 'identity/cjsf/keepAlive', { headers })
      .pipe(
        map((result: any) => {
        }),
        catchError(this.handleError),
        finalize(() => {
          this.refreshInProgress = false;
        })
      )
      .subscribe();
  }

  private handleError(error: Response | any) {
    // Todo: We should use a remote logging infrastructure
    // In a real world app, we might use a remote logging infrastructure
    // We'd also dig deeper into the error to get a better message
    const errMsg = error.message
      ? error.message
      : error.status
        ? `${error.status} - ${error.statusText}`
        : 'Server error';
    console.error('stateService - handlError: ' + errMsg); // log to console for now
    return throwError(error);
    // Todo: How to handle this properly in component?
  }

  public async login(username: string, password: string, code?: string) {
    var url = this.hostPath + 'identity/cjsf/token';
    var data = 'site=www.shuttlebuss.se&username=' + encodeURIComponent(username) + '&password=' + encodeURIComponent(password) + '&smscode=' + code;
    var headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    try {
      const result = await firstValueFrom(this.http.post<any>(url, data, { headers }));
      const token = result?.access_token;
      if (token) {
        this.authService.setToken(token);
        const config = await this.myConfigService.fetchAndSetMyConfig();
        if (!config) {
          this.logout();
          return {
            status: 0,
            success: false,
            message: 'Failed to load configuration'
          };
        }
        this.eventQueue.dispatch(new AppEvent(AppEventType.LoginSuccess, true));
      }
      return result;
    } catch (error: any) {
      if (error instanceof HttpErrorResponse && error.error && error.error.status) {
        return error.error;
      }
      return {
        status: 0,
        success: false
      };
    }
  }

  public async getAndSetMyConfig() {
    return this.myConfigService.fetchAndSetMyConfig();
  }

  public logout(): Observable<void> {
    return this.http.delete<void>(`${this.hostPath}identity/cjsf/revoketoken`).pipe(
      finalize(() => {
        this.authService.logout();
      })
    );
  }

  public forgotPassword(email: string): Observable<LoginResult> {
    const data = { site: 'www.shuttlebuss.se', email, language: 'Svenska' };
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .post(this.hostPath + 'identity/cjsf/user/forgotpassword', data, { headers })
      .pipe(
        map((result: any) => {
          const loginResult = new LoginResult();
          loginResult.success = result.success;
          loginResult.userCreateStatus = result.userCreateStatus;
          loginResult.status = result.status;
          return loginResult;
        }),
        catchError((error: any) => {
          const loginResult = new LoginResult();
          const result = error;
          loginResult.success = result.success;
          loginResult.userCreateStatus = result.userCreateStatus;
          loginResult.status = result.status;
          return throwError(loginResult);
        }),
        finalize(() => { })
      );
  }

  public resetPassword(code: string, password: string): Observable<LoginResult> {
    const data = { site: 'www.shuttlebuss.se', code, password };
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const loginResult = new LoginResult();
    return this.http
      .post(`${this.hostPath}identity/cjsf/user/resetpassword`, data, { headers })
      .pipe(
        map((result: any) => {
          loginResult.success = result.success;
          loginResult.userCreateStatus = result.userCreateStatus;
          loginResult.status = result.status;
          return loginResult;
        }),
        catchError((error: any) => {
          const result = error;
          loginResult.success = result.success;
          loginResult.userCreateStatus = result.userCreateStatus;
          loginResult.status = result.status;
          return throwError(loginResult);
        })
      );
  }
}
